import { useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { buildLoginUrl } from '@/lib/redirect';
import { useMyProducers } from '@/hooks/useMyProducers';
import { useProducerEntryState } from '@/hooks/useProducerEntryState';
import { useAuth } from '@/context/AuthContext';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { PageContainer } from '@/components/layout/PageContainer';
import { Sprout, AlertCircle, RefreshCw, ArrowRight, ArrowLeft } from 'lucide-react';

const LAST_PRODUCER_KEY = 'farmly:lastProducerId';

/**
 * ProducerPortal – state-driven entry point for the host experience.
 *
 * Scenario C (active_host): redirect to farm dashboard.
 * Scenario B (in_progress): show "continue" view with contextual CTA.
 * Scenario A (not_host): show onboarding start with "Kom igång".
 */
export default function ProducerPortal() {
  const { isLoading: authLoading, user } = useAuth();
  const { producers, hasProducers, isLoading: producersLoading, error, refetch } = useMyProducers();
  const { state: entryState, isLoading: entryLoading, reason } = useProducerEntryState();
  const navigate = useNavigate();

  const hasRedirected = useRef(false);
  const isLoading = authLoading || producersLoading || entryLoading;

  // ── Scenario C: active_host → redirect to farm dashboard ──
  useEffect(() => {
    if (isLoading || hasRedirected.current || !hasProducers) return;

    let targetProducerId: string | null = null;
    try {
      const lastId = localStorage.getItem(LAST_PRODUCER_KEY);
      if (lastId && producers.some(p => p.id === lastId)) {
        targetProducerId = lastId;
      }
    } catch { /* ignore */ }

    if (!targetProducerId && producers.length > 0) {
      targetProducerId = producers[0].id;
    }

    if (targetProducerId) {
      hasRedirected.current = true;
      try { localStorage.setItem(LAST_PRODUCER_KEY, targetProducerId); } catch { /* ignore */ }
      navigate(`/app/producer/farm/${targetProducerId}`, { replace: true });
    }
  }, [isLoading, hasProducers, producers, navigate]);

  // ── Loading ──
  if (isLoading || (hasProducers && !hasRedirected.current)) {
    return (
      <PageContainer>
        <div className="space-y-4">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-6 w-64" />
          <Skeleton className="h-24 w-full rounded-lg mt-8" />
        </div>
      </PageContainer>
    );
  }

  // ── Not logged in ──
  if (!user) {
    return (
      <PageContainer>
        <Card className="max-w-md mx-auto border-border/60 shadow-none">
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Sprout className="h-12 w-12 text-muted-foreground mb-4" />
            <h2 className="font-display text-xl font-medium mb-2">Värdportal</h2>
            <p className="text-sm text-muted-foreground max-w-sm mb-6">
              Du måste vara inloggad för att komma åt värdportalen.
            </p>
            <Button asChild className="w-full max-w-xs">
              <Link to={buildLoginUrl('/app/producer')}>Logga in</Link>
            </Button>
          </CardContent>
        </Card>
      </PageContainer>
    );
  }

  // ── Error ──
  if (error) {
    return (
      <PageContainer>
        <Card className="max-w-md mx-auto border-border/60 shadow-none">
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <AlertCircle className="h-12 w-12 text-destructive mb-4" />
            <h2 className="font-display text-xl font-medium mb-2">Kunde inte ladda</h2>
            <p className="text-muted-foreground max-w-sm mb-6 text-sm">
              {(error as Error).message || 'Ett oväntat fel inträffade.'}
            </p>
            <Button onClick={() => refetch()}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Försök igen
            </Button>
          </CardContent>
        </Card>
      </PageContainer>
    );
  }

  // ── Scenario B: in_progress ──
  if (entryState === 'in_progress') {
    return (
      <PageContainer>
        <div className="max-w-md mx-auto py-8 space-y-6">
          <Link
            to="/app/customer"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Tillbaka
          </Link>
          <div className="space-y-3">
            <h1 className="font-display text-2xl text-foreground leading-tight">
              Fortsätt bli värd
            </h1>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {reason === 'invitation'
                ? 'Du har en inbjudan att acceptera.'
                : 'Vi har tagit emot ditt intresse. Vi återkommer via e-post.'}
            </p>
          </div>

          <div className="flex flex-col gap-3 pt-4">
            {reason === 'invitation' ? (
              <Button asChild className="h-11">
                <Link to="/app/customer/invitations">
                  Visa inbjudan
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            ) : (
              <Button asChild variant="outline" className="h-11">
                <Link to="/for-producers/qualify">
                  Se status
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            )}
            <Link
              to="/for-producers"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors text-center"
            >
              Så fungerar det →
            </Link>
          </div>
        </div>
      </PageContainer>
    );
  }

  // ── Scenario A: not_host ──
  return (
    <PageContainer>
      <div className="max-w-md mx-auto py-8 space-y-6">
        <Link
          to="/app/customer"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Tillbaka
        </Link>
        <div className="space-y-3">
          <Sprout className="h-10 w-10 text-muted-foreground" />
          <h1 className="font-display text-2xl text-foreground leading-tight">
            Bli värd i Farmly
          </h1>
          <p className="text-sm text-muted-foreground leading-relaxed max-w-sm">
            Erbjud din plats eller dina aktiviteter via Farmly.
          </p>
        </div>

        <div className="flex flex-col gap-3 pt-4">
          <Button asChild className="h-11">
            <Link to="/for-producers/qualify">
              Kom igång
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </PageContainer>
  );
}
